import { useMsal } from '@azure/msal-react'
import { Bell, BellOff, Check, Copy, Download, Share, ShieldCheck } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useCallback, useEffect, useRef, useState } from 'react'
import reinzLogo from '@/assets/reinz-logo.png'
import { clearConfig } from '@/Config/ConfigService'
import { trackEvent } from '@/lib/analytics'
import { clearAppLocalStorage } from '@/lib/storage'
import {
  getPushPermissionState,
  isPushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from '@/pushNotifications'

interface NotificationItem {
  id: number
  type: string
  title: string
  body?: string
  url?: string
  publishedAt: string
}

interface AppHeaderProps {
  title?: string
  userName?: string
  onAdminAccess?: () => void
  isAdmin?: boolean
  onLogout?: () => void
}

const TITLES = new Set(['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'rev'])
function getInitial(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  const first = parts.find((p) => !TITLES.has(p.toLowerCase().replace(/\.$/, '')))
  return first ? first.charAt(0).toUpperCase() : '?'
}

// Detect if already installed (running in standalone/fullscreen = already on home screen)
function isInstalled(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
}

// Detect iOS Safari
function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream
}

// Type → display label and colour
const TYPE_BADGE: Record<string, { label: string; className: string }> = {
  news: { label: 'News', className: 'bg-blue-100 text-blue-700' },
  event: { label: 'Event', className: 'bg-green-100 text-green-700' },
  education: { label: 'Education', className: 'bg-orange-100 text-orange-700' },
  cpd: { label: 'CPD', className: 'bg-purple-100 text-purple-700' },
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes

export function AppHeader({ title, userName, onAdminAccess, isAdmin, onLogout }: AppHeaderProps) {
  const { instance, accounts } = useMsal()
  const [isOpen, setIsOpen] = useState(false)
  const [isBellOpen, setIsBellOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [snapshotUnreadCount, setSnapshotUnreadCount] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLDivElement>(null)
  const [installPrompt, setInstallPrompt] = useState<any>(null)
  const [showIosInstructions, setShowIosInstructions] = useState(false)
  const [isPushSubscribed, setIsPushSubscribed] = useState(false)
  const [pushToggleLoading, setPushToggleLoading] = useState(false)
  const [expandedNotif, setExpandedNotif] = useState<NotificationItem | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [copied, setCopied] = useState(false)

  // Get B2C objectId — use accounts[0] (reactive) instead of getActiveAccount()
  // which may return null at render time before MSAL has set the active account.
  const userId = accounts[0]?.localAccountId ?? ''

  // Check on mount whether the browser already has an active push subscription
  useEffect(() => {
    if (!isPushSupported() || getPushPermissionState() !== 'granted') return
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setIsPushSubscribed(!!sub)),
    )
  }, [])

  const fetchFeed = useCallback(async () => {
    if (!userId) return
    try {
      const res = await fetch(`/api/notifications/feed?userId=${encodeURIComponent(userId)}`)
      if (!res.ok) return
      const data = await res.json()
      setNotifications(data.items ?? [])
      setUnreadCount(data.unreadCount ?? 0)
    } catch {
      // Silently ignore — bell just shows no items
    }
  }, [userId])

  // Fetch on mount then poll every 5 minutes
  useEffect(() => {
    fetchFeed()
    const interval = setInterval(fetchFeed, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchFeed])

  // Open bell when user clicks a push notification toast
  useEffect(() => {
    // Case 1: app was already open — service worker sends a message
    function handleSwMessage(event: MessageEvent) {
      if (event.data?.type === 'OPEN_NOTIFICATIONS') {
        setIsBellOpen(true)
        fetchFeed()
      }
    }
    navigator.serviceWorker?.addEventListener('message', handleSwMessage)

    // Case 2: app was opened fresh via /?openNotifications=true
    const params = new URLSearchParams(window.location.search)
    if (params.get('openNotifications') === 'true') {
      setIsBellOpen(true)
      // Clean the query param from the URL without a page reload
      window.history.replaceState({}, '', window.location.pathname)
    }

    return () => navigator.serviceWorker?.removeEventListener('message', handleSwMessage)
  }, [fetchFeed])

  const handleBellOpen = async () => {
    setIsBellOpen((prev) => {
      if (!prev) trackEvent('notification_bell_opened', {})
      if (prev) setSnapshotUnreadCount(0) // reset highlights on close
      return !prev
    })
    setIsOpen(false)

    // Mark all as seen in DB — unread count resets
    if (userId && unreadCount > 0) {
      setSnapshotUnreadCount(unreadCount)
      setUnreadCount(0)
      try {
        await fetch('/api/notifications/seen', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        })
      } catch {
        // Non-critical — bell still works
      }
    }
  }

  const handlePushToggle = async () => {
    if (!userId) return
    setPushToggleLoading(true)
    try {
      if (isPushSubscribed) {
        await unsubscribeFromPush(userId)
        setIsPushSubscribed(false)
        trackEvent('push_notifications_disabled', {})
      } else {
        const permission =
          getPushPermissionState() === 'granted'
            ? 'granted'
            : await Notification.requestPermission()
        if (permission === 'granted') {
          await subscribeToPush(userId)
          setIsPushSubscribed(true)
          trackEvent('push_notifications_enabled', {})
        }
      }
    } catch (err) {
      console.error('Push toggle failed:', err)
    } finally {
      setPushToggleLoading(false)
    }
  }

  // Capture the Android install prompt event before Chrome shows it
  useEffect(() => {
    function handleBeforeInstallPrompt(e: Event) {
      e.preventDefault()
      setInstallPrompt(e)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  }, [])

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setShowIosInstructions(false)
      }
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setIsBellOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInstallClick = async () => {
    if (isIos()) {
      setShowIosInstructions((prev) => !prev)
    } else if (installPrompt) {
      installPrompt.prompt()
      const { outcome } = await installPrompt.userChoice
      if (outcome === 'accepted') setInstallPrompt(null)
      setIsOpen(false)
    }
  }

  const showInstallOption = !isInstalled() && (isIos() || !!installPrompt)

  return (
    <>
      <header className="sticky top-0 z-40 bg-card/95 backdrop-blur-xs border-b border-border safe-top">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 min-w-0 overflow-hidden">
            <img src={reinzLogo} alt="REINZ" className="h-6 w-auto shrink-0" />
            {title && (
              <span className="text-sm font-semibold text-foreground truncate sm:text-lg font-heading">
                {title}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Bell notifications */}
            <div className="relative" ref={bellRef}>
              <button
                onClick={handleBellOpen}
                className="relative w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
              >
                <Bell className="w-6 h-6 fill-yellow-500 text-yellow-600" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-3xs font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {isBellOpen && (
                <div className="absolute -right-12 mt-1 w-80 max-w-[calc(100vw-1rem)] bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">REINZ Notices</p>
                    {notifications.length > 0 && (
                      <span className="text-3xs text-muted-foreground">
                        {notifications.length} item{notifications.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-xs text-muted-foreground">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.map((n, idx) => {
                        const isUnread = idx < snapshotUnreadCount
                        const badge = TYPE_BADGE[n.type] ?? {
                          label: n.type,
                          className: 'bg-muted text-muted-foreground',
                        }
                        const content = (
                          <div
                            className={`flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-muted/40 transition-colors cursor-pointer ${isUnread ? 'bg-primary/15' : ''}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <span
                                  className={`px-1.5 py-0.5 text-3xs font-semibold rounded-full ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                                <span className="text-3xs text-muted-foreground">
                                  {formatDate(n.publishedAt)}
                                </span>
                                {isUnread && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                )}
                              </div>
                              <p
                                className={`text-xs line-clamp-2 ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}
                              >
                                {n.title}
                              </p>
                              {n.body && (
                                <p className="text-3xs text-muted-foreground mt-0.5 line-clamp-1">
                                  {n.body}
                                </p>
                              )}
                            </div>
                          </div>
                        )

                        return n.url ? (
                          <a
                            key={n.id}
                            href={n.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                            onClick={() =>
                              trackEvent('notification_opened', { type: n.type, title: n.title })
                            }
                          >
                            {content}
                          </a>
                        ) : (
                          <div
                            key={n.id}
                            onClick={() => {
                              trackEvent('notification_opened', { type: n.type, title: n.title })
                              setExpandedNotif(n)
                            }}
                          >
                            {content}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Push toggle — only shown if browser supports push */}
                  {isPushSupported() && getPushPermissionState() !== 'denied' && (
                    <div className="border-t border-border">
                      <button
                        onClick={handlePushToggle}
                        disabled={pushToggleLoading}
                        className="w-full flex items-center gap-2 px-4 py-3 text-xs text-foreground font-medium hover:bg-muted/40 transition-colors disabled:opacity-50"
                      >
                        {isPushSubscribed ? (
                          <>
                            <BellOff className="w-3.5 h-3.5 fill-yellow-500 text-yellow-600" />
                            {pushToggleLoading ? 'Turning off…' : 'Turn off push notifications'}
                          </>
                        ) : (
                          <>
                            <Bell className="w-3.5 h-3.5 fill-yellow-500 text-yellow-600" />
                            {pushToggleLoading ? 'Turning on…' : 'Turn on push notifications'}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Profile dropdown */}
            <div className="relative" ref={dropdownRef}>
              <div
                className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium cursor-pointer"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                {getInitial(userName)}
              </div>

              {isOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-card border border-border rounded shadow-md z-50">
                  {/* Install App — Android or iOS */}
                  {showInstallOption && (
                    <div>
                      <button
                        className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        onClick={handleInstallClick}
                      >
                        {isIos() ? (
                          <Share className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <Download className="w-4 h-4 text-muted-foreground" />
                        )}
                        Install App
                      </button>

                      {/* iOS instructions popover */}
                      {showIosInstructions && (
                        <div className="mx-2 mb-2 p-3 bg-muted rounded text-xs text-foreground space-y-1">
                          <p className="font-semibold">Add to Home Screen:</p>
                          <p>
                            1. Tap the <strong>Share</strong> button (⬆) in Safari
                          </p>
                          <p>
                            2. Select <strong>"Add to Home Screen"</strong>
                          </p>
                        </div>
                      )}

                      <div className="border-t border-border" />
                    </div>
                  )}

                  {/* Admin Panel — only visible to admins */}
                  {isAdmin && (
                    <div>
                      <button
                        className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center gap-2"
                        onClick={() => {
                          setIsOpen(false)
                          onAdminAccess?.()
                        }}
                      >
                        <ShieldCheck className="w-4 h-4 text-primary" />
                        Admin Panel
                      </button>
                      <div className="border-t border-border" />
                    </div>
                  )}

                  {/* Share App — mobile: native share, desktop: QR modal */}
                  <div>
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-muted flex items-center gap-2"
                      onClick={() => {
                        setIsOpen(false)
                        setShowShareModal(true)
                      }}
                    >
                      <Share className="w-4 h-4 text-muted-foreground" />
                      Share App
                    </button>
                    <div className="border-t border-border" />
                  </div>

                  {/* Logout */}
                  <button
                    className="w-full text-left px-4 py-2 text-sm hover:bg-muted"
                    onClick={() => {
                      onLogout?.()
                      clearConfig()
                      sessionStorage.clear()
                      clearAppLocalStorage()
                      instance.logoutRedirect({ account: instance.getActiveAccount() })
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Notification detail modal — rendered outside header so it covers the full screen */}
      {expandedNotif && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 px-4">
          <div
            className="bg-card rounded-xl shadow-xl w-full max-w-sm p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`px-1.5 py-0.5 text-3xs font-semibold rounded-full ${(TYPE_BADGE[expandedNotif.type] ?? { className: 'bg-muted text-muted-foreground' }).className}`}
              >
                {(TYPE_BADGE[expandedNotif.type] ?? { label: expandedNotif.type }).label}
              </span>
              <span className="text-3xs text-muted-foreground">
                {formatDate(expandedNotif.publishedAt)}
              </span>
            </div>
            <p className="text-sm font-semibold text-foreground">{expandedNotif.title}</p>
            {expandedNotif.body && (
              <p className="text-sm text-muted-foreground leading-relaxed">{expandedNotif.body}</p>
            )}
            <button
              onClick={() => setExpandedNotif(null)}
              className="w-full mt-2 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Share App QR modal — desktop only */}
      {showShareModal && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="bg-card rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-semibold text-foreground">Share REINZ Companion</p>
            <p className="text-xs text-muted-foreground text-center">
              Scan this QR code on your phone to open the app
            </p>
            <QRCodeSVG value="https://mobile.reinz.co.nz" size={180} />
            <button
              onClick={() => {
                navigator.clipboard.writeText('https://mobile.reinz.co.nz')
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-muted-foreground" />
              )}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
            <button
              onClick={() => setShowShareModal(false)}
              className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  )
}
